import { useRef, useState } from 'react';
import type { TrialOffer } from '../plan-v2/trial-offer';
import { ActivateNowModal } from './ActivateNowModal';
import { TrialUpgradeStrip } from './TrialUpgradeStrip';

interface Props {
  agencyId: string;
  offer: TrialOffer;
  /** Reads the subscription and the usage again. */
  onReload: () => void;
  /** Leaves for the card flow ("Trocar cartão"). */
  onChangeCard: () => void;
}

/**
 * The strip and its confirmation, wired to the tab.
 *
 * The tab is reloaded when the dialog is closed after an activation, not at
 * the instant it succeeds: the reload puts the whole tab on its skeleton, and
 * that would unmount the dialog before its success message was read. A
 * decline reloads nothing (RN-10: nothing changed); "not in trial" reloads at
 * once, since the offer is no longer true.
 *
 * @param props - The offer and what the tab does around it.
 * @returns The strip and, while open, the dialog.
 */
export function TrialActivation({ agencyId, offer, onReload, onChangeCard }: Props) {
  const [open, setOpen] = useState(false);
  const activated = useRef(false);

  const close = () => {
    setOpen(false);
    if (activated.current) onReload();
    activated.current = false;
  };

  return (
    <>
      <TrialUpgradeStrip offer={offer} onActivate={() => setOpen(true)} />
      {open && (
        <ActivateNowModal
          agencyId={agencyId}
          offer={offer}
          onClose={close}
          onActivated={() => { activated.current = true; }}
          onNotInTrial={() => { setOpen(false); onReload(); }}
          onChangeCard={() => { setOpen(false); onChangeCard(); }}
        />
      )}
    </>
  );
}
